/**
 * @class ShopifyAPI
 * @description 提供了与 Shopify 购物车交互的静态方法。
 */

/**
 * Safe price parser for handling both EU and US formats
 * @param {string|number} value - Price value (could be "64,00", "64.00", or 64)
 * @returns {number} - Parsed price as number
 */
function parsePriceSafe(value) {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const strValue = String(value).trim();

  // Check if it's a formatted price with currency (fallback to CurrencyUtils)
  if (/[€$£¥₹]/.test(strValue) || /EUR|USD|GBP/i.test(strValue)) {
    return window.CurrencyUtils ? window.CurrencyUtils.parsePrice(strValue) : parseFloat(strValue.replace(/[^\d\.]/g, ''));
  }

  // Handle EU format with comma as decimal separator (e.g., "64,00")
  if (/^\d+,\d{2}$/.test(strValue)) {
    return parseFloat(strValue.replace(',', '.'));
  }

  // Handle standard number format
  return parseFloat(strValue) || 0;
}

class ShopifyAPI {
  /**
   * 执行单个商品添加到购物车的 AJAX 请求。
   * @param {string} variantId - 变体 ID。
   * @param {number} quantity - 数量。
   * @param {string} productTitle - 产品标题。
   * @param {HTMLElement | null} button - 触发操作的按钮元素。
   * @returns {Promise<object>} - Shopify 返回的数据。
   */
  static executeAddToCart(variantId, quantity, productTitle, button = null) {
    if (button) {
      button.classList.add("loading");
      button.disabled = true;
    }

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", quantity);

    let sections = "cart-icon-bubble";
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      sections += `,${cartDrawer
        .closest(".shopify-section")
        .id.replace("shopify-section-", "")}`;
    }
    formData.append("sections", sections);
    formData.append("sections_url", window.location.pathname);

    return fetch(window.theme?.routes?.cartAdd || "/cart/add.js", {
      method: "POST",
      headers: {
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && data.status !== "success") {
          console.error("添加购物车失败:", data.description);
          this.handleButtonState(button, "error");
          alert(data.description || "添加购物车失败");
          return Promise.reject(data.description);
        }
        this.updateCartIcon(data);
        this.handleButtonState(button, "success");
        if (cartDrawer) {
          setTimeout(() => cartDrawer.renderContents(data, true), 700);
        }
        return data;
      })
      .catch((error) => {
        console.error("添加购物车请求失败:", error);
        this.handleButtonState(button, "error");
        alert("添加购物车请求失败，请稍后再试");
        return Promise.reject(error);
      });
  }

  /**
   * 执行批量商品添加到购物车的 AJAX 请求。
   * @param {Array<object>} products - 商品数组 [{ variantId, quantity }]。
   * @param {HTMLElement | null} button - 触发操作的按钮元素。
   * @returns {Promise<object>} - Shopify 返回的数据。
   */
  static bulkAddToCart(products, button = null) {
    if (!products || !products.length) return Promise.resolve();

    if (button) {
      button.classList.add("loading");
      button.disabled = true;
    }

    const items = products.map((p) => ({
      id: p.variantId,
      quantity: p.quantity,
    }));

    let sections = "cart-icon-bubble";
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      sections += `,${cartDrawer
        .closest(".shopify-section")
        .id.replace("shopify-section-", "")}`;
    }

    if (items.length === 1) {
      // 单个商品使用FormData方式提交
      const formData = new FormData();
      formData.append("id", items[0].id);
      formData.append("quantity", items[0].quantity);
      formData.append("sections", sections);
      formData.append("sections_url", window.location.pathname);

      return fetch(window.theme?.routes?.cartAdd || "/cart/add.js", {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status && data.status !== "success") {
            console.error("批量添加购物车失败:", data.description);
            this.handleButtonState(button, "error");
            return Promise.reject(data.description);
          }
          this.updateCartIcon(data);
          if (cartDrawer) {
            setTimeout(() => cartDrawer.renderContents(data, true), 700);
          }
          this.handleButtonState(button, "success");
          return data;
        })
        .catch((error) => {
          console.error("批量添加购物车请求失败:", error);
          this.handleButtonState(button, "error");
          alert("批量添加购物车请求失败，请稍后再试");
          return Promise.reject(error);
        });
    } else {
      // 多个商品使用JSON格式提交
      return fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: items,
          sections: sections,
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status && data.status !== "success") {
            console.error("批量添加购物车失败:", data.description);
            this.handleButtonState(button, "error");
            return Promise.reject(data.description);
          }
          this.updateCartIcon(data);
          if (cartDrawer) {
            setTimeout(() => cartDrawer.renderContents(data, true), 700);
          }
          this.handleButtonState(button, "success");
          return data;
        })
        .catch((error) => {
          console.error("批量添加购物车请求失败:", error);
          this.handleButtonState(button, "error");
          alert("批量添加购物车请求失败，请稍后再试");
          return Promise.reject(error);
        });
    }
  }

  /**
   * 更新购物车图标。
   * @param {object} data - Shopify 返回的数据。
   */
  static updateCartIcon(data) {
    const cartIconBubble = document.getElementById("cart-icon-bubble");
    if (cartIconBubble && data.sections && data.sections["cart-icon-bubble"]) {
      cartIconBubble.innerHTML = data.sections["cart-icon-bubble"];
      return;
    }
    // Fallback
    const count = data.item_count || 0;
    document
      .querySelectorAll(".header__cart-count-number")
      .forEach((el) => (el.textContent = count));
    document
      .querySelectorAll(".header__cart-count .visually-hidden")
      .forEach(
        (el) => (el.textContent = `${count} item${count === 1 ? "" : "s"}`)
      );
    document
      .querySelectorAll(".header__cart-count")
      .forEach((el) => el.classList.toggle("hidden", count === 0));
  }

  /**
   * 处理按钮状态（加载、成功、错误）。
   * @param {HTMLElement | null} button - 按钮元素。
   * @param {'loading' | 'success' | 'error' | 'reset'} state - 状态。
   */
  static handleButtonState(button, state) {
    if (!button) return;

    button.classList.remove("loading", "success", "error");
    button.disabled = false;

    switch (state) {
      case "loading":
        button.classList.add("loading");
        button.disabled = true;
        break;
      case "success":
        button.classList.add("success");
        setTimeout(() => {
          button.classList.remove("success");
          button.disabled = false;
        }, 1000);
        break;
      case "error":
        button.classList.add("error");
        setTimeout(() => {
          button.classList.remove("error");
          button.disabled = false;
        }, 1000);
        break;
      case "reset":
      default:
        // Already reset
        break;
    }
  }
}

/**
 * @class ProductRecommender
 * @description 管理单个产品推荐区块的逻辑和状态。
 */
class ProductRecommender {
  /**
   * @param {HTMLElement} element - 产品推荐区块的根元素 (.rp-widget 或 .rp-product-flex)。
   * @param {string} mode - 模式：'explore'（弹窗模式）或 'inline'（直接显示模式）
   */
  constructor(element, mode = 'explore') {
    this.mode = mode; // 'explore' 或 'inline'

    if (mode === 'explore') {
      // Explore 模式：element 是 .rp-widget
      this.widget = element;
      this.blockId = this.widget.dataset.blockId;
      this.currency = this.widget.dataset.currency || "";
      this.modal = document.getElementById(`rp-modal-${this.blockId}`);
      this.productWrappers = [];
      this.modalProductWrappers = this.modal
        ? this.modal.querySelectorAll(".rp-product-flex")
        : [];
      this.exploreBtn = this.widget.querySelector(".rp-explore-btn");
      this.closeBtn = this.modal
        ? this.modal.querySelector(".rp-modal__close")
        : null;
      this.modalAddBtn = this.modal
        ? this.modal.querySelector(".rp-modal__add-btn")
        : null;
      this.totalPriceEl = this.modal
        ? this.modal.querySelector(`#rp-total-price-${this.blockId} .rp-modal__price-value`)
        : null;
      this.totalItemsEl = this.modal
        ? this.modal.querySelector(`#rp-total-items-${this.blockId}`)
        : null;
      this.originalPriceEl = this.modal
        ? this.modal.querySelector(`#rp-original-price-${this.blockId}`)
        : null;
      this.originalPriceValueEl = this.modal
        ? this.modal.querySelector(`#rp-original-price-${this.blockId} .rp-modal__original-price-value`)
        : null;
    } else if (mode === 'inline') {
      // Inline 模式：element 是 .rp-product-flex
      this.widget = null;
      this.blockId = null;
      this.currency = element.dataset.currency || "";
      this.modal = null;
      this.productWrappers = [element];
      this.modalProductWrappers = [];
      this.exploreBtn = null;
      this.closeBtn = null;
      this.modalAddBtn = null;
      this.totalPriceEl = null;
      this.totalItemsEl = null;
    }

    // 每个实例维护自己的独立状态
    this.state = {
      selectedProducts: [], // [{ productId, variantId, quantity, price, compareAtPrice, title, variantTitle, variantColor }]
      totalPrice: 0,
      totalOriginalPrice: 0, // 新增：总原价
      totalItems: 0
    };

    // 标识该实例是否准备好加购
    // inline 模式：点击 add 时立即设置为 true
    // explore 模式：点击 "ADD TO CART" 时设置为 true
    this.readyForCheckout = false;

    this.init();
  }

  /**
   * 初始化，绑定所有事件监听器。
   */
  init() {
    // 绑定主页面和弹窗内的产品交互
    this.productWrappers.forEach((wrapper) =>
      this.bindProductFlexHandlers(wrapper)
    );
    this.modalProductWrappers.forEach((wrapper) =>
      this.bindProductFlexHandlers(wrapper)
    );

    // 绑定弹窗控制按钮
    if (this.exploreBtn) {
      this.exploreBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.openModal();
      });
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => this.closeModal());
    }
    if (this.modal) {
      this.modal.addEventListener("click", (e) => {
        if (e.target === this.modal) this.closeModal();
      });
    }
    if (this.modalAddBtn) {
      this.modalAddBtn.addEventListener("click", () =>
        this.handleBulkAddToCart()
      );
    }

    // 绑定全局点击事件来关闭下拉菜单
    document.addEventListener("click", (e) => {
      if (!e.target.closest(".re-product-item__variants-dropdown")) {
        const activeDropdowns = document.querySelectorAll(
          ".re-product-item__variants-dropdown.active"
        );
        activeDropdowns.forEach((dropdown) =>
          dropdown.classList.remove("active")
        );
      }
    });

    // 注册到全局管理器
    if (window.RecommendedProducts) {
      window.RecommendedProducts.registerInstance(this);
    }
  }

  /**
   * 为产品容器绑定事件处理器 (使用事件委托)。
   * @param {HTMLElement} productWrapper - 产品容器元素 (.rp-product-flex)。
   */
  bindProductFlexHandlers(productWrapper) {
    if (
      !productWrapper ||
      productWrapper.classList.contains("re-handler-attached")
    )
      return;
    productWrapper.classList.add("re-handler-attached");

    // 使用事件委托处理所有点击事件
    productWrapper.addEventListener("click", (e) => {
      const target = e.target;

      // 处理 Checkbox 点击（快速选择）
      if (target.classList.contains("re-product-item__quick-select-input")) {
        const checkbox = target;
        const productItem = checkbox.closest(".re-product-item");
        if (!productItem) return;

        // 找到对应的 Add to Cart 按钮来触发加购逻辑
        const addBtn = productItem.querySelector(".re-btn--add-to-cart");
        if (!addBtn) return;

        // 检查是否单变体产品
        const dropdown = productItem.querySelector(".re-product-item__variants-dropdown");
        const variantsCount = parseInt(dropdown?.dataset.variantsCount || "0");

        // 如果 checkbox 被选中，触发添加；如果取消选中，触发移除
        if (checkbox.checked) {
          // 单变体和多变体都直接选择当前显示的变体
          // 延迟触发，确保checkbox状态已更新
          setTimeout(() => {
            this.handleAddToCartClick(addBtn);
          }, 0);
        } else {
          // 取消选中：移除该产品的所有变体
          const productId = addBtn.dataset.productId;
          const variantId = addBtn.dataset.product;
          if (productId && variantId) {
            this.removeProduct(productId, variantId);
          }
        }
        return;
      }

      // 处理变体下拉菜单的点击
      const variantDropdown = target.closest(
        ".re-product-item__variants-dropdown"
      );
      if (variantDropdown) {
        // 切换下拉菜单的显示状态
        if (
          target.closest(".re-product-item__variants-selected") ||
          target.closest(".re-product-item__variant-arrow")
        ) {
          e.stopPropagation();
          variantDropdown.classList.toggle("active");
          return;
        }

        // 处理选项点击
        if (target.classList.contains("re-product-item__variants-option") ||
          target.closest(".re-product-item__variants-option")) {
          e.stopPropagation();
          // 获取选项元素（可能是子元素被点击）
          const option = target.classList.contains("re-product-item__variants-option") ?
            target : target.closest(".re-product-item__variants-option");

          // 更新选中状态
          const options = variantDropdown.querySelectorAll(
            ".re-product-item__variants-option"
          );
          options.forEach((opt) => opt.classList.remove("selected"));
          option.classList.add("selected");

          // 关闭下拉菜单
          variantDropdown.classList.remove("active");

          // 触发变体变更处理
          this.handleVariantChangeCustom(variantDropdown, option);
          return;
        }
      }

      // 数量按钮
      if (target.classList.contains("re-quantity-btn")) {
        this.handleQuantityChange(target);
        return;
      }

      // 添加到购物车按钮
      if (target.classList.contains("re-btn--add-to-cart")) {
        this.handleAddToCartClick(target);
        return;
      }

      // "Explore" 模式下移除按钮
      if (target.classList.contains("rp-item-remove-selected") ||
        target.closest(".rp-item-remove-selected")) {
        const removeBtn = target.classList.contains("rp-item-remove-selected") ?
          target : target.closest(".rp-item-remove-selected");
        const variantId = removeBtn.dataset.variantId;
        const productItem = removeBtn.closest(".re-product-item");
        const productId = productItem?.querySelector(".re-btn--add-to-cart")
          ?.dataset.productId;
        if (variantId && productId) {
          this.removeProduct(productId, variantId);
        }
        return;
      }
    });
  }

  /**
   * 处理自定义变体选择变化。
   * @param {HTMLElement} dropdown - 变体下拉菜单元素。
   * @param {HTMLElement} selectedOption - 选中的选项元素。
   */
  handleVariantChangeCustom(dropdown, selectedOption) {
    const productItem = dropdown.closest(".re-product-item");
    if (!productItem) return;

    const addBtn = productItem.querySelector(".re-btn--add-to-cart");

    if (addBtn) {
      addBtn.setAttribute("data-product", selectedOption.dataset.value);
      addBtn.setAttribute("data-price", selectedOption.dataset.priceRaw || "");
      addBtn.setAttribute("data-compare-at-price", selectedOption.dataset.compareAtPriceRaw || "");
    }

    // 更新变体标题显示
    const variantTitle = selectedOption.dataset.title || selectedOption.textContent.trim();
    const variantTitleEl = productItem.querySelector(
      ".re-product-item__variants-selected-text"
    );
    if (variantTitleEl) {
      variantTitleEl.textContent = variantTitle;
    }

    // 更新产品卡片信息
    this.updateProductCardInfo(productItem, selectedOption);

    // 处理颜色变体的圆球显示
    this.updateColorSwatch(productItem, selectedOption);
  }

  /**
   * 更新产品卡片信息
   * @param {HTMLElement} productItem - 产品项元素
   * @param {HTMLElement} selectedOption - 选中的选项元素
   */
  updateProductCardInfo(productItem, selectedOption) {
    // 更新当前价格 - 直接使用 Liquid 格式化好的价格字符串
    const priceCurrentEl = productItem.querySelector(".re-product-item__price-current");
    if (priceCurrentEl && selectedOption.dataset.price) {
      priceCurrentEl.innerHTML = `<span class="money">${selectedOption.dataset.price}</span>`;
    }

    // 更新原价（仅当有原价时）- 直接使用 Liquid 格式化好的价格字符串
    const priceOldEl = productItem.querySelector(".re-product-item__price-old");
    if (priceOldEl) {
      const comparePrice = selectedOption.dataset.compareAtPrice;
      const comparePriceRaw = parseFloat(selectedOption.dataset.compareAtPriceRaw || "0");
      if (comparePrice && comparePriceRaw > 0) {
        priceOldEl.innerHTML = `<span class="money">${comparePrice}</span>`;
      } else {
        priceOldEl.innerHTML = "";
      }
    }

    // 更新图片
    const imageEl = productItem.querySelector(".re-product-item__image");
    if (imageEl && selectedOption.dataset.image) {
      imageEl.src = selectedOption.dataset.image;
    }

    // 更新描述
    const descEl = productItem.querySelector(".re-product-item__description");
    if (descEl && selectedOption.dataset.description) {
      descEl.textContent = selectedOption.dataset.description;
    }
  }

  /**
   * 更新颜色变体圆球显示
   * @param {HTMLElement} productItem - 产品项元素
   * @param {HTMLElement} selectedOption - 选中的选项元素
   */
  updateColorSwatch(productItem, selectedOption) {
    const colorSwatch = selectedOption.querySelector(".variant-color-swatch");
    const targetContainer = productItem.querySelector(
      ".re-product-item__variants-selected"
    );

    // 移除现有的色块
    const existingSwatch = targetContainer.querySelector(".variant-color-swatch");
    if (existingSwatch) {
      existingSwatch.remove();
    }

    // 如果有颜色色块，添加到选中区域
    if (colorSwatch) {
      const newSwatch = colorSwatch.cloneNode(true);
      targetContainer.insertBefore(newSwatch, targetContainer.firstChild);
    }
  }

  /**
   * 从变体标题中提取颜色代码
   * @param {string} variantTitle - 变体标题
   * @returns {string|null} - 颜色代码或null
   */
  extractColorFromTitle(variantTitle) {
    if (!variantTitle) return null;

    const titleParts = variantTitle.split('(');
    if (titleParts.length > 1) {
      const colorPart = titleParts[1].split(')')[0];
      // 检查是否是颜色代码格式
      if (colorPart.includes('#')) {
        return colorPart.trim();
      } else if (colorPart.length === 6 && /^[0-9A-Fa-f]{6}$/.test(colorPart)) {
        return `#${colorPart}`;
      }
    }
    return null;
  }

  /**
   * 处理数量变化。
   * @param {HTMLElement} btn - 数量按钮。
   */
  handleQuantityChange(btn) {
    const productItem = btn.closest(".re-product-item");
    const input = productItem?.querySelector('input[type="number"]');
    if (!input) return;

    let value = parseInt(input.value) || 1;
    const action = btn.dataset.action;

    if (action === "increment") value++;
    else if (action === "decrement" && value > 1) value--;

    input.value = value;
  }

  /**
   * 处理添加到购物车按钮点击。
   * 核心改动：移除独立加购逻辑，只添加到选中列表
   * @param {HTMLElement} btn - 添加按钮。
   */
  handleAddToCartClick(btn) {
    const productItem = btn.closest(".re-product-item");
    if (!productItem) return;

    const productId = btn.dataset.productId;
    const variantId = btn.dataset.product;
    const price = btn.dataset.price;
    const compareAtPrice = btn.dataset.compareAtPrice || "0"; // 获取原价
    const quantityInput = productItem.querySelector(`#quantity-${productId}`);
    const quantity = parseInt(quantityInput?.value || "1");
    const productTitle =
      productItem.querySelector(".re-product-item__title h6")?.textContent || "";

    // 获取变体标题
    const variantTitleEl = productItem.querySelector(".re-product-item__variants-selected-text");
    const variantTitle = variantTitleEl ? variantTitleEl.textContent.trim() : productTitle;

    // 获取颜色色块
    const colorSwatch = productItem.querySelector(".re-product-item__variants-selected .variant-color-swatch");
    const variantColor = colorSwatch ? colorSwatch.style.backgroundColor : null;

    // Inline 模式：点击 add 时立即设置为 true，准备加购（必须在 addProduct 之前）
    if (this.mode === 'inline') {
      this.readyForCheckout = true;
      console.log(`[${this.mode}] readyForCheckout set to true BEFORE addProduct`);
    }

    // 【核心修改】只添加到选中列表，不调用 ShopifyAPI 加购
    // 实际加购由主产品的 Add to Cart 按钮统一触发
    this.addProduct({
      productId,
      variantId,
      quantity,
      price,
      compareAtPrice, // 新增：传递原价
      title: productTitle,
      variantTitle,
      variantColor,
      productItemElement: productItem,
    });
  }

  /**
   * 添加商品到当前实例的状态（独立管理）
   * @param {object} productData - 商品数据。
   */
  addProduct(productData) {
    // Explore 模式：添加新产品时，重置为未确认状态
    // 必须重新点击 "ADD TO CART" 才能确认
    if (this.mode === 'explore' && this.readyForCheckout) {
      this.readyForCheckout = false;
      console.log(`[${this.mode}] readyForCheckout reset to false - new product added to unconfirmed state`);
    }

    const existingIndex = this.state.selectedProducts.findIndex(
      (p) =>
        p.variantId === productData.variantId &&
        p.productId === productData.productId
    );

    if (existingIndex >= 0) {
      // 累加数量
      this.state.selectedProducts[existingIndex].quantity +=
        productData.quantity;
    } else {
      // 添加新产品到选中列表
      this.state.selectedProducts.push({
        productId: productData.productId,
        variantId: productData.variantId,
        quantity: productData.quantity,
        price: productData.price,
        compareAtPrice: productData.compareAtPrice || "0", // 新增：存储原价
        title: productData.title,
        variantTitle: productData.variantTitle,
        variantColor: productData.variantColor
      });
    }

    // 只更新当前实例的UI
    const productItem = this.findProductItemByIds(productData.productId, productData.variantId);
    if (productItem) {
      this.updateSingleProductItemDisplay(productItem);
      this.updateCheckboxAndHighlight(productData.productId, productData.variantId, true);
    }

    // 更新状态（总价、总数）
    this.updateTotalState();

    // 更新 Explore 模式的 Modal UI（但不更新外层 Widget，只在点击 ADD TO CART 时更新）
    if (this.mode === 'explore') {
      this.updateModalAndWidgetUI();
      // 注意：不再立即调用 updateOuterWidgetDisplay()
      // 外层 Widget 只在点击 ADD TO CART 按钮时更新
      // Explore 模式下也不立即更新 Footer 价格，只在点击 ADD TO CART 时更新
    } else {
      // Inline 模式下立即触发 Footer 价格更新
      this.triggerFooterPriceUpdate();
    }
  }

  /**
   * 从当前实例的状态移除商品（独立管理）
   * @param {string} productId - 父产品 ID。
   * @param {string} variantId - 变体 ID。
   * @param {boolean} updateOuterWidget - 是否更新外层 Widget（默认 false）。
   */
  removeProduct(productId, variantId, updateOuterWidget = false) {
    // Explore 模式：从 modal 内删除产品时，重置为未确认状态
    // 必须重新点击 "ADD TO CART" 才能确认
    if (this.mode === 'explore' && this.readyForCheckout && !updateOuterWidget) {
      this.readyForCheckout = false;
      console.log(`[${this.mode}] readyForCheckout reset to false - product removed from modal`);
    }

    const index = this.state.selectedProducts.findIndex(
      (p) => p.variantId === variantId && p.productId === productId
    );

    if (index !== -1) {
      this.state.selectedProducts.splice(index, 1);

      // 只更新当前实例的UI
      const productItem = this.findProductItemByIds(productId, variantId);
      if (productItem) {
        this.updateSingleProductItemDisplay(productItem);
      }

      // 检查该产品是否还有其他变体被选中
      const remainingVariants = this.state.selectedProducts.filter(p => p.productId === productId);
      if (remainingVariants.length === 0) {
        this.updateCheckboxAndHighlight(productId, null, false);
      }

      // 更新状态（总价、总数）
      this.updateTotalState();

      // 更新 Explore 模式的 Modal UI
      if (this.mode === 'explore') {
        this.updateModalAndWidgetUI();
        // 只有在指定更新外层 Widget 时才更新（比如从外层删除按钮点击）
        if (updateOuterWidget) {
          this.updateOuterWidgetDisplay();
          // 从外层删除时，触发 Footer 价格更新
          this.triggerFooterPriceUpdate();
        }
        // 从 modal 内删除时，不更新 Footer 价格，只在点击 ADD TO CART 时更新
      } else {
        // Inline 模式下立即触发 Footer 价格更新
        this.triggerFooterPriceUpdate();
      }
    }
  }

  /**
   * 查找匹配指定产品ID和变体ID的产品项元素
   * @param {string} productId - 产品ID
   * @param {string} variantId - 变体ID
   * @returns {HTMLElement|null}
   */
  findProductItemByIds(productId, variantId) {
    const containers = this.mode === 'explore'
      ? [this.modal]
      : this.productWrappers;

    for (const container of containers) {
      if (!container) continue;
      const btn = container.querySelector(`.re-btn--add-to-cart[data-product-id="${productId}"]`);
      if (btn) {
        return btn.closest('.re-product-item');
      }
    }
    return null;
  }

  /**
   * 更新总状态（总价、总数）- 适用于所有模式
   */
  updateTotalState() {
    let total = 0;
    let originalTotal = 0;
    let itemsCount = 0;
    this.state.selectedProducts.forEach((p) => {
      // 使用安全的价格解析函数，支持EU和US格式
      const price = parsePriceSafe(p.price);
      const compareAtPrice = parsePriceSafe(p.compareAtPrice || p.price);

      total += price * p.quantity;
      originalTotal += compareAtPrice * p.quantity;
      itemsCount += p.quantity;
    });

    this.state.totalPrice = total;
    this.state.totalOriginalPrice = originalTotal;
    this.state.totalItems = itemsCount;

    console.log(`[${this.mode}] State updated - Total: ${total.toFixed(2)}, Original: ${originalTotal.toFixed(2)}, Items: ${itemsCount}`, this.state.selectedProducts);
  }

  /**
   * 更新 Modal UI (总价, 总数显示)（仅 Explore 模式）
   */
  updateModalAndWidgetUI() {
    // 检测当前页面的货币格式（EU使用逗号，US使用点号）
    const detectCurrencyFormat = () => {
      // 尝试从主产品价格中检测格式
      const priceEl = document.querySelector('.product-info__price .price__current, .re-product-item__price-current');
      if (priceEl) {
        const priceText = priceEl.textContent;
        // 检测是否使用逗号作为小数分隔符
        if (/\d,\d{2}(?:\s|EUR|$)/.test(priceText)) {
          return 'EU'; // 欧洲格式
        }
      }
      // 默认使用货币代码判断
      return this.currency === 'EUR' ? 'EU' : 'US';
    };

    const formatPriceForDisplay = (amount) => {
      const format = detectCurrencyFormat();
      if (format === 'EU') {
        // 欧洲格式：使用逗号作为小数分隔符
        return amount.toFixed(2).replace('.', ',');
      } else {
        // 美国格式：使用点号作为小数分隔符
        return amount.toFixed(2);
      }
    };

    // 更新当前价格
    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = formatPriceForDisplay(this.state.totalPrice);
    }

    // 更新 total items
    if (this.totalItemsEl) {
      this.totalItemsEl.textContent = `Total ${this.state.totalItems} item(s) selected`;
    }

    // 更新原价
    if (this.originalPriceValueEl && this.originalPriceEl) {
      // 检查是否有任何产品具有对比价格（原价）
      const hasAnyComparePrice = this.state.selectedProducts.some(p => {
        const compareAtPrice = parsePriceSafe(p.compareAtPrice || "0");
        const price = parsePriceSafe(p.price || "0");
        return compareAtPrice > 0 && compareAtPrice > price;
      });

      // 只要有任何产品有对比价格，就显示总原价
      if (hasAnyComparePrice && this.state.totalOriginalPrice > 0) {
        this.originalPriceValueEl.textContent = formatPriceForDisplay(this.state.totalOriginalPrice);
        this.originalPriceEl.classList.add('show');
      } else {
        this.originalPriceEl.classList.remove('show');
      }
    }

    // 更新按钮状态
    if (this.modalAddBtn) {
      const hasSelection = this.state.selectedProducts.length > 0;
      this.modalAddBtn.disabled = !hasSelection;
      if (hasSelection) {
        this.modalAddBtn.classList.add("rp-modal__add-btn-hover");
      } else {
        this.modalAddBtn.classList.remove("rp-modal__add-btn-hover");
      }
    }
  }

  /**
   * 更新总状态和全局 UI (总价, 总数)（仅 Explore 模式）
   * @deprecated 保留用于兼容性，建议使用 updateTotalState + updateModalAndWidgetUI
   */
  updateStateAndUI() {
    this.updateTotalState();
    this.updateModalAndWidgetUI();
  }

  /**
   * 更新单个产品项下已选变体的信息显示。
   * @param {HTMLElement} productItemElement - 产品项元素。
   */
  updateSingleProductItemDisplay(productItemElement) {
    if (!productItemElement) return;

    const productId = productItemElement.querySelector(".re-btn--add-to-cart")
      ?.dataset.productId;
    if (!productId) return;

    const variantsForThisItem = this.state.selectedProducts.filter(
      (p) => p.productId === productId
    );

    const infoElement = productItemElement.querySelector(
      ".re-product-item__info"
    );
    if (!infoElement) return;

    let container = infoElement.querySelector(
      ".rp-product-item-all-selected-variants"
    );
    if (!container) {
      container = document.createElement("div");
      container.className = "rp-product-item-all-selected-variants";
      infoElement.appendChild(container);
    }

    container.innerHTML = "";

    if (variantsForThisItem.length > 0) {
      const totalQty = variantsForThisItem.reduce(
        (acc, v) => acc + v.quantity,
        0
      );

      // 显示已选变体信息，包括颜色色块
      const variantDivs = variantsForThisItem.map((variant) => {
        const variantDiv = document.createElement("div");
        variantDiv.className = "rp-product-item-selected-info";

        // 构建颜色色块HTML
        const colorSwatchHTML = variant.variantColor ?
          `<span class="rp-selected-info-color"><span class="variant-color-swatch" style="background-color: ${variant.variantColor};"></span></span>` : '';

        variantDiv.innerHTML = `
          ${colorSwatchHTML}
          <span class="rp-selected-info-title">
            ${variant.variantTitle}
          </span>
          <div class="rp-selected-info-quantity-container">
            <span class="rp-selected-info-quantity">× ${variant.quantity}</span>
            <button class="rp-item-remove-selected" data-variant-id="${variant.variantId}">
              <svg class="trash-icon" viewBox="0 0 24 24" width="16" height="16">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        `;
        // container.appendChild(variantDiv);
        return variantDiv;
      });

      container.innerHTML = `<div class="rp-selected-items-summary">${totalQty} item(s) selected</div><div class="rp-selected-items-list">${variantDivs.map(variantDiv => variantDiv.outerHTML).join('')}</div>`;
    }
  }

  /**
   * 更新checkbox选中状态（只影响当前实例）
   * @param {string} productId - 产品ID
   * @param {string} variantId - 变体ID
   * @param {boolean} isSelected - 是否选中
   */
  updateCheckboxAndHighlight(productId, variantId, isSelected) {
    // 【修复】只在当前实例的容器内查找和更新checkbox
    // 避免影响其他实例中的相同产品
    const containers = this.mode === 'explore'
      ? [this.modal, this.widget].filter(Boolean)  // Explore: modal + widget (过滤null)
      : this.productWrappers;                      // Inline: productWrappers

    containers.forEach(container => {
      if (!container) return;

      // 在当前容器内查找匹配的产品
      const btn = container.querySelector(`.re-btn--add-to-cart[data-product-id="${productId}"]`);
      if (btn) {
        const productItem = btn.closest('.re-product-item');
        if (!productItem) return;

        const checkbox = productItem.querySelector('.re-product-item__quick-select-input');
        if (checkbox) {
          checkbox.checked = isSelected;
        }
        productItem.classList.toggle('re-product-item--selected', !!isSelected);
      }
    });
  }

  /**
   * 更新外层Widget显示（Explore模式）
   */
  updateOuterWidgetDisplay() {
    const mainCheckbox = document.querySelector(`#rp-main-checkbox-${this.blockId}`);
    const selectedItemsContainer = document.querySelector(`#rp-widget-selected-${this.blockId}`);
    const selectedCountEl = selectedItemsContainer?.querySelector('.rp-widget__selected-count');
    const selectedListEl = selectedItemsContainer?.querySelector('.rp-widget__selected-list');
    const exploreBtnText = document.querySelector(`.rp-explore-btn[data-block-id="${this.blockId}"] .rp-explore-btn-text`);
    const exploreBtnTextMore = document.querySelector(`.rp-explore-btn[data-block-id="${this.blockId}"] .rp-explore-btn-text-more`);

    if (!selectedItemsContainer) return;

    // 没有选中产品
    if (this.state.selectedProducts.length === 0) {
      mainCheckbox && (mainCheckbox.checked = false);
      selectedItemsContainer.style.display = 'none';
      if (exploreBtnText) exploreBtnText.style.display = 'inline';
      if (exploreBtnTextMore) exploreBtnTextMore.style.display = 'none';
      if (this.modalAddBtn) this.modalAddBtn.disabled = true;
      return;
    }

    // 有选中产品
    mainCheckbox && (mainCheckbox.checked = true);
    selectedItemsContainer.style.display = 'block';
    if (exploreBtnText) exploreBtnText.style.display = 'none';
    if (exploreBtnTextMore) exploreBtnTextMore.style.display = 'inline';
    if (this.modalAddBtn) this.modalAddBtn.disabled = false;

    // 更新计数
    if (selectedCountEl) {
      selectedCountEl.textContent = `${this.state.totalItems} item(s) selected`;
    }

    // 更新列表
    if (selectedListEl) {
      selectedListEl.innerHTML = '';

      this.state.selectedProducts.forEach(product => {
        const itemDiv = document.createElement('div');
        itemDiv.className = 'rp-widget__selected-item';

        const colorSwatchHTML = product.variantColor ?
          `<span class="variant-color-swatch" style="background-color: ${product.variantColor};"></span>` : '';

        itemDiv.innerHTML = `
          <div class="rp-widget__selected-item-title">
            ${colorSwatchHTML}
            <span>${product.variantTitle}</span>
          </div>
          <div class="rp-widget__selected-item-quantity">x${product.quantity}</div>
          <button class="rp-widget__selected-item-delete" data-product-id="${product.productId}" data-variant-id="${product.variantId}">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none">
              <path stroke="#333" stroke-linejoin="round" d="M2.625 2.916v9.917h8.75V2.916z"></path>
              <path stroke="#333" stroke-linecap="round" stroke-linejoin="round" d="M5.833 5.834v3.792M8.167 5.834v3.792M1.167 2.916h11.667"></path>
              <path stroke="#333" stroke-linejoin="round" d="m4.667 2.916.96-1.75h2.767l.94 1.75z"></path>
            </svg>
          </button>
        `;

        const deleteBtn = itemDiv.querySelector('.rp-widget__selected-item-delete');
        deleteBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          // 从外层 Widget 删除时，需要更新外层 Widget 显示
          this.removeProduct(product.productId, product.variantId, true);
        });

        selectedListEl.appendChild(itemDiv);
      });
    }
  }

  /**
   * 打开弹窗。
   */
  openModal() {
    if (this.modal) {
      this.modal.classList.add("modal-open");
      // 移除 body overflow hidden 以避免页面宽度变化
      // document.body.style.overflow = "hidden";
    }
  }

  /**
   * 关闭弹窗。
   */
  closeModal() {
    if (this.modal) {
      this.modal.classList.remove("modal-open");
      // document.body.style.overflow = "";
    }
  }

  /**
   * 处理批量添加到购物车（弹窗模式）
   * 核心改动：移除独立加购，只关闭弹窗
   * 实际加购由主产品按钮统一触发
   */
  handleBulkAddToCart() {
    if (this.state.selectedProducts.length === 0) return;

    // 【核心修改】只关闭弹窗，不调用 ShopifyAPI
    // 用户需要点击主产品的 Add to Cart 按钮才能真正加购
    this.closeModal();

    // Explore 模式：点击 "ADD TO CART" 时设置为 true，准备加购
    this.readyForCheckout = true;
    console.log(`[${this.mode}] readyForCheckout set to true after ADD TO CART click`);

    // 更新外层Widget显示
    this.updateOuterWidgetDisplay();

    // 【新增】触发 Footer 价格更新
    // 只有在点击 ADD TO CART 按钮时才更新 Footer 价格
    this.triggerFooterPriceUpdate();
  }

  /**
   * 触发 Footer 价格更新
   * 调用 variant-picker 的 updateFixedFooterPrice 方法
   */
  triggerFooterPriceUpdate() {
    try {
      const recommendedTotal = window.RecommendedProducts?.getRecommendedProductsTotalPrice() || 0;
      console.log(`[${this.mode}] Triggering footer price update - Current instance total: ${this.state.totalPrice.toFixed(2)}, Global total: ${recommendedTotal.toFixed(2)}`);

      // 方法1：直接调用 variant-picker 实例的方法
      const variantPicker = document.querySelector('variant-picker');

      if (variantPicker && typeof variantPicker.updateFixedFooterPrice === 'function') {
        console.log('[RecommendedProducts] Calling variant-picker.updateFixedFooterPrice()');
        variantPicker.updateFixedFooterPrice();
      } else {
        console.warn('[RecommendedProducts] variant-picker element not found or updateFixedFooterPrice method missing');

        // 备用方案：直接查找并更新 footer 价格元素
        const footerPriceEl = document.querySelector('#footer-product-price');
        if (footerPriceEl) {
          const priceCurrentEl = document.querySelector('.product-info__price .price__current');
          if (priceCurrentEl) {
            const mainPriceText = priceCurrentEl.textContent;
            // 使用 CurrencyUtils 正确解析和格式化价格
            const mainPriceAmount = window.CurrencyUtils ?
              window.CurrencyUtils.parsePrice(mainPriceText) :
              parseFloat(mainPriceText.replace(/[^\d\.]/g, "")) || 0;
            const recommendedTotal = window.RecommendedProducts?.getRecommendedProductsTotalPrice() || 0;
            const totalAmount = mainPriceAmount + recommendedTotal;

            footerPriceEl.textContent = window.CurrencyUtils ?
              window.CurrencyUtils.formatPrice(totalAmount, mainPriceText) :
              `${mainPriceText.match(/[^\d\.\,\s]+/)?.[0] || ''}${totalAmount.toFixed(2)}`;
            console.log('[RecommendedProducts] Updated footer price directly:', totalAmount);
          }
        }
      }

      // 方法2：触发自定义事件（用于其他监听器）
      document.dispatchEvent(new CustomEvent('recommended-products:selection-changed', {
        detail: {
          totalPrice: window.RecommendedProducts?.getRecommendedProductsTotalPrice() || 0,
          totalItems: window.RecommendedProducts?.getRecommendedProductsTotalItems() || 0
        }
      }));
    } catch (error) {
      console.error('[RecommendedProducts] Error updating footer price:', error);
    }
  }
}

/**
 * 初始化所有产品推荐区块
 */
document.addEventListener("DOMContentLoaded", function () {
  // 初始化 Explore 模式（expand_by_default=true）
  const widgets = document.querySelectorAll(".rp-widget");
  widgets.forEach((widget) => {
    new ProductRecommender(widget, 'explore');
  });

  // 初始化 Inline 模式（expand_by_default=false）
  // 查找不在 .rp-widget 和 .rp-modal 内的 .rp-product-flex
  const standaloneFlexes = document.querySelectorAll(
    ".rp-product-flex:not(.rp-widget .rp-product-flex):not(.rp-modal .rp-product-flex)"
  );
  standaloneFlexes.forEach((flex) => {
    new ProductRecommender(flex, 'inline');
  });
});
